import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import { ListPageLayout } from "@dashboard/components/Layouts/List/Root";
import { ResponsiveTable } from "@dashboard/components/ResponsiveTable/ResponsiveTable";
import { TableButtonWrapper } from "@dashboard/components/TableButtonWrapper/TableButtonWrapper";
import TableRowLink from "@dashboard/components/TableRowLink/TableRowLink";
import { type AtticAssetDirectoryFragment, type AtticAssetStoreFragment } from "@dashboard/graphql";
import { sectionNames } from "@dashboard/intl";
import { renderCollection } from "@dashboard/misc";
import { visuallyHidden } from "@dashboard/utils/visuallyHidden";
import { TableBody, TableCell, TableHead } from "@material-ui/core";
import {
  Box,
  Button,
  EditIcon,
  Skeleton,
  Text,
  Tooltip,
  TrashBinIcon,
} from "@saleor/macaw-ui-next";
import { useIntl } from "react-intl";

import { badgeMessages, directoryListMessages, storeMessages } from "../../messages";
import { atticAssetDirectoryUrl } from "../../urls";
import { formatBytes, getDirectoryRemovalRefusal, getUsagePercent } from "../../utils";
import { AtticAssetDirectoryBadges } from "../AtticAssetDirectoryBadges/AtticAssetDirectoryBadges";

const numberOfColumns = 6;

export interface AtticAssetDirectoryListPageProps {
  store: AtticAssetStoreFragment | undefined;
  /** undefined while loading. */
  directories: AtticAssetDirectoryFragment[] | undefined;
  loadFailed: boolean;
  onAdd: () => void;
  onRename: (directory: AtticAssetDirectoryFragment) => void;
  onDelete: (directory: AtticAssetDirectoryFragment) => void;
}

/** Bytes in use against the advisory limit, and where the files are served from. */
const StoreSummary = ({ store }: { store: AtticAssetStoreFragment | undefined }) => {
  const intl = useIntl();

  if (!store) {
    return (
      <Box paddingX={6} paddingY={4}>
        <Skeleton />
      </Box>
    );
  }

  const percent = getUsagePercent(store.byteCount, store.byteLimit);

  return (
    <Box
      paddingX={6}
      paddingY={4}
      display="grid"
      __gridTemplateColumns="minmax(0, 1fr) minmax(0, 1fr)"
      gap={6}
      data-test-id="attic-asset-store-summary"
    >
      <Box display="flex" flexDirection="column" gap={2}>
        <Text size={2} color="default2">
          {intl.formatMessage(storeMessages.storage)}
        </Text>
        <Text size={3}>
          {intl.formatMessage(storeMessages.usage, {
            used: formatBytes(store.byteCount, intl.locale),
            limit: formatBytes(store.byteLimit, intl.locale),
            percent,
          })}
        </Text>
        <Box
          __height="6px"
          borderRadius={2}
          backgroundColor="default2"
          overflow="hidden"
          __maxWidth="320px"
        >
          <Box
            height="100%"
            backgroundColor={percent >= 90 ? "critical1" : "accent1"}
            __width={`${percent}%`}
          />
        </Box>
      </Box>
      <Box display="flex" flexDirection="column" gap={2}>
        <Text size={2} color="default2">
          {intl.formatMessage(storeMessages.cdnOrigin)}
        </Text>
        <Text size={3} __wordBreak="break-all">
          {store.cdnBase ?? intl.formatMessage(storeMessages.cdnUnknown)}
        </Text>
      </Box>
    </Box>
  );
};

const refusalMessages = {
  default: directoryListMessages.deleteRefusedDefault,
  managed: directoryListMessages.deleteRefusedManaged,
  notEmpty: directoryListMessages.deleteRefusedNotEmpty,
  busy: directoryListMessages.deleteRefusedBusy,
} as const;

export const AtticAssetDirectoryListPage = ({
  store,
  directories,
  loadFailed,
  onAdd,
  onRename,
  onDelete,
}: AtticAssetDirectoryListPageProps) => {
  const intl = useIntl();
  // Why a directory cannot be deleted, in merchant words decided from its
  // structured fields; the backend's sentence is never shown or branched on.
  const describeRefusal = (directory: AtticAssetDirectoryFragment) => {
    const refusal = getDirectoryRemovalRefusal(directory);

    return refusal
      ? intl.formatMessage(refusalMessages[refusal], {
          name: directory.claimedBy || intl.formatMessage(badgeMessages.managedByApp),
        })
      : null;
  };

  return (
    <ListPageLayout>
      <TopNav title={intl.formatMessage(sectionNames.atticDigitalAssets)}>
        <Box display="flex" alignItems="center" gap={3}>
          <Button onClick={onAdd} data-test-id="attic-asset-directory-add">
            {intl.formatMessage(directoryListMessages.addDirectory)}
          </Button>
        </Box>
      </TopNav>
      <DashboardCard data-test-id="attic-asset-directory-list">
        <DashboardCard.Content paddingX={0}>
          {loadFailed ? (
            <Box paddingX={6} paddingY={4}>
              <Text size={3} color="critical1">
                {intl.formatMessage(directoryListMessages.loadFailed)}
              </Text>
            </Box>
          ) : (
            <>
              <StoreSummary store={store} />
              <ResponsiveTable>
                <TableHead>
                  <TableRowLink>
                    <TableCell>{intl.formatMessage(directoryListMessages.name)}</TableCell>
                    <TableCell>{intl.formatMessage(directoryListMessages.folder)}</TableCell>
                    <TableCell align="right">
                      {intl.formatMessage(directoryListMessages.files)}
                    </TableCell>
                    <TableCell align="right">
                      {intl.formatMessage(directoryListMessages.size)}
                    </TableCell>
                    <TableCell>{intl.formatMessage(directoryListMessages.access)}</TableCell>
                    <TableCell />
                  </TableRowLink>
                </TableHead>
                <TableBody>
                  {renderCollection(
                    directories,
                    row => (
                      <TableRowLink
                        key={row ? row.id : "skeleton"}
                        hover={!!row}
                        href={row ? atticAssetDirectoryUrl(row.id) : undefined}
                        data-test-id="attic-asset-directory-row"
                      >
                        <TableCell>{row ? row.name : <Skeleton />}</TableCell>
                        <TableCell>
                          {row ? (
                            <Text size={3} color="default2">
                              /{row.slug}
                            </Text>
                          ) : (
                            <Skeleton />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {row ? intl.formatNumber(row.objectCount) : <Skeleton />}
                        </TableCell>
                        <TableCell align="right">
                          {row ? formatBytes(row.byteCount, intl.locale) : <Skeleton />}
                        </TableCell>
                        <TableCell>
                          {row ? <AtticAssetDirectoryBadges directory={row} /> : <Skeleton />}
                        </TableCell>
                        <TableCell align="right">
                          {row && (
                            <Box display="flex" gap={1} justifyContent="flex-end">
                              {row.writable && (
                                <TableButtonWrapper>
                                  <Button
                                    variant="tertiary"
                                    icon={<EditIcon />}
                                    title={intl.formatMessage(directoryListMessages.rename)}
                                    aria-label={intl.formatMessage(directoryListMessages.rename)}
                                    onClick={() => onRename(row)}
                                    data-test-id="attic-asset-directory-rename"
                                  />
                                </TableButtonWrapper>
                              )}
                              {row.removable ? (
                                <TableButtonWrapper>
                                  <Button
                                    variant="tertiary"
                                    icon={<TrashBinIcon />}
                                    title={intl.formatMessage(directoryListMessages.delete)}
                                    aria-label={intl.formatMessage(
                                      directoryListMessages.deleteLabel,
                                      {
                                        name: row.name,
                                      },
                                    )}
                                    onClick={() => onDelete(row)}
                                    data-test-id="attic-asset-directory-delete"
                                  />
                                </TableButtonWrapper>
                              ) : (
                                // Focusable (aria-disabled, not disabled) so a
                                // keyboard or screen-reader user reaches the
                                // reason too; a click does nothing and never
                                // follows the row's link.
                                <Tooltip>
                                  <Tooltip.Trigger>
                                    <Box>
                                      <TableButtonWrapper>
                                        <Button
                                          variant="tertiary"
                                          icon={<TrashBinIcon />}
                                          aria-disabled
                                          aria-label={intl.formatMessage(
                                            directoryListMessages.deleteLabel,
                                            { name: row.name },
                                          )}
                                          aria-describedby={`attic-asset-directory-refusal-${row.id}`}
                                          onClick={() => undefined}
                                          __opacity="0.5"
                                          cursor="not-allowed"
                                          data-test-id="attic-asset-directory-delete-refused"
                                        />
                                      </TableButtonWrapper>
                                      {/* Always in the DOM, unlike the tooltip. */}
                                      <span
                                        id={`attic-asset-directory-refusal-${row.id}`}
                                        style={visuallyHidden}
                                      >
                                        {describeRefusal(row)}
                                      </span>
                                    </Box>
                                  </Tooltip.Trigger>
                                  <Tooltip.Content side="left">
                                    <Tooltip.Arrow />
                                    {describeRefusal(row)}
                                  </Tooltip.Content>
                                </Tooltip>
                              )}
                            </Box>
                          )}
                        </TableCell>
                      </TableRowLink>
                    ),
                    () => (
                      <TableRowLink data-test-id="attic-asset-directory-list-empty">
                        <TableCell colSpan={numberOfColumns}>
                          <Text size={3} color="default2">
                            {intl.formatMessage(directoryListMessages.noDirectories)}
                          </Text>
                        </TableCell>
                      </TableRowLink>
                    ),
                  )}
                </TableBody>
              </ResponsiveTable>
            </>
          )}
        </DashboardCard.Content>
      </DashboardCard>
    </ListPageLayout>
  );
};

export default AtticAssetDirectoryListPage;
